import React, { useMemo, useState } from 'react';
import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  styled,
} from '@mui/material';
import { moneyFormat } from 'helpers/moneyFormat';
import { BudgetImportPendingItem, BudgetImportReport, BudgetImportRowResult } from 'types/budgetImport';

const Cards = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
}));

const Card = styled('div')(({ theme }) => ({
  padding: theme.spacing(1.25, 1.5),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  background: theme.palette.grey[50],
}));

const Section = styled('section')({ display: 'flex', flexDirection: 'column', gap: 8 });

const TableScroll = styled('div')({ overflowX: 'auto' });

const STATUS = {
  ok: { label: 'Ok', color: 'success' },
  warning: { label: 'Aviso', color: 'warning' },
  error: { label: 'Erro', color: 'error' },
} as const;

const PENDING: { key: keyof BudgetImportReport['pending']; title: string }[] = [
  { key: 'hospitals', title: 'Hospitais não cadastrados' },
  { key: 'doctors', title: 'Médicos não cadastrados' },
  { key: 'procedures', title: 'Procedimentos não cadastrados' },
  { key: 'specialties', title: 'Especialidades não cadastradas' },
  { key: 'patients', title: 'Pacientes para criar ou completar' },
];

function describe(item: BudgetImportPendingItem) {
  const detail = [item.cpf && `CPF ${item.cpf}`, item.cnpj && `CNPJ ${item.cnpj}`, item.crm && `CRM ${item.crm}`]
    .filter(Boolean)
    .join(' · ');
  const rows = item.rows.filter(Boolean);

  return [
    item.name || 'Sem nome',
    detail,
    item.suggestion && `parece "${item.suggestion}"`,
    item.duplicated && 'cadastrado mais de uma vez',
    rows.length && `orçamento${rows.length > 1 ? 's' : ''} ${rows.join(', ')}`,
  ]
    .filter(Boolean)
    .join(' · ');
}

interface ImportReportProps {
  report: BudgetImportReport;
}

const ImportReport: React.FC<ImportReportProps> = ({ report }) => {
  const [filter, setFilter] = useState<'all' | BudgetImportRowResult['status']>('all');
  const rows = useMemo(
    () => (filter === 'all' ? report.rows : report.rows.filter(row => row.status === filter)),
    [report.rows, filter],
  );
  const { summary } = report;

  return (
    <>
      <Cards>
        <Card>
          <Typography variant="caption" color="textSecondary">
            Orçamentos na planilha
          </Typography>
          <Typography variant="h6">{summary.total}</Typography>
        </Card>
        <Card>
          <Typography variant="caption" color="textSecondary">
            Prontos
          </Typography>
          <Typography variant="h6" color="success.main">
            {summary.ok}
          </Typography>
        </Card>
        <Card>
          <Typography variant="caption" color="textSecondary">
            Com aviso
          </Typography>
          <Typography variant="h6" color="warning.main">
            {summary.warning}
          </Typography>
        </Card>
        <Card>
          <Typography variant="caption" color="textSecondary">
            Com erro (não entram)
          </Typography>
          <Typography variant="h6" color="error">
            {summary.error}
          </Typography>
        </Card>
      </Cards>

      {summary.issues.length > 0 && (
        <Section>
          <Typography variant="subtitle2" fontWeight={700}>
            O que apareceu
          </Typography>
          {summary.issues.map(issue => (
            <div key={issue.code} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Chip
                size="small"
                color={issue.level === 'error' ? 'error' : 'warning'}
                label={`${issue.count} ${issue.count > 1 ? 'orçamentos' : 'orçamento'}`}
              />
              <Typography variant="body2">{issue.message}</Typography>
            </div>
          ))}
        </Section>
      )}

      {PENDING.filter(({ key }) => report.pending[key].length > 0).map(({ key, title }) => (
        <Section key={key}>
          <Typography variant="subtitle2" fontWeight={700}>
            {title} ({report.pending[key].length})
          </Typography>
          {report.pending[key].map((item, index) => (
            <Typography key={index} variant="body2">
              {describe(item)}
            </Typography>
          ))}
        </Section>
      ))}

      <Section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Orçamentos
          </Typography>
          <ToggleButtonGroup exclusive size="small" value={filter} onChange={(_, value) => value && setFilter(value)}>
            <ToggleButton value="all">Todos</ToggleButton>
            <ToggleButton value="error">Erros</ToggleButton>
            <ToggleButton value="warning">Avisos</ToggleButton>
            <ToggleButton value="ok">Prontos</ToggleButton>
          </ToggleButtonGroup>
        </div>

        <TableScroll>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nº</TableCell>
                <TableCell>Paciente</TableCell>
                <TableCell>Procedimento</TableCell>
                <TableCell align="right">Venda</TableCell>
                <TableCell>Situação</TableCell>
                <TableCell>Detalhes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.number} hover>
                  <TableCell>{row.number}</TableCell>
                  <TableCell>{row.patient_name || '-'}</TableCell>
                  <TableCell>{row.procedure || '-'}</TableCell>
                  <TableCell align="right">{row.value_sale ? moneyFormat(row.value_sale) : '-'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={STATUS[row.status].label} color={STATUS[row.status].color} />
                  </TableCell>
                  <TableCell>
                    {row.issues.map(issue => (
                      <Typography
                        key={issue.code}
                        variant="caption"
                        display="block"
                        color={issue.level === 'error' ? 'error' : 'textSecondary'}
                      >
                        {issue.message}
                      </Typography>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableScroll>
      </Section>
    </>
  );
};

export default ImportReport;
